import type { PixelData } from '@vikings/types';

// ═══════════════════════════════════════════════
// Pre-built pixel-art assets as inline data
// No external files needed — all art defined here
// ═══════════════════════════════════════════════

/** Transparent pixel marker (used in pixel arrays) */
const _: null = null;

/**
 * Coin animation frames (8x8, 4 frames for rotation illusion)
 */
export function getCoinFrames(): PixelData[] {
  const C = '#c9a654'; // Gold
  const D = '#a07830'; // Dark gold
  const L = '#ffe066'; // Light gold

  // Frame 1: Full coin face
  const frame1: PixelData = {
    width: 8,
    height: 8,
    pixels: [
      [_, _, C, C, C, C, _, _],
      [_, C, L, L, L, L, C, _],
      [C, L, L, D, D, L, L, C],
      [C, L, D, L, L, D, L, C],
      [C, L, D, L, L, D, L, C],
      [C, L, L, D, D, L, L, C],
      [_, C, L, L, L, L, C, _],
      [_, _, C, C, C, C, _, _],
    ],
  };

  // Frame 2: Slightly narrower (3/4 turn)
  const frame2: PixelData = {
    width: 8,
    height: 8,
    pixels: [
      [_, _, _, C, C, _, _, _],
      [_, _, C, L, L, C, _, _],
      [_, C, L, D, D, L, C, _],
      [_, C, D, L, L, D, C, _],
      [_, C, D, L, L, D, C, _],
      [_, C, L, D, D, L, C, _],
      [_, _, C, L, L, C, _, _],
      [_, _, _, C, C, _, _, _],
    ],
  };

  // Frame 3: Edge (thin line)
  const frame3: PixelData = {
    width: 8,
    height: 8,
    pixels: [
      [_, _, _, C, _, _, _, _],
      [_, _, _, D, _, _, _, _],
      [_, _, _, D, _, _, _, _],
      [_, _, _, C, _, _, _, _],
      [_, _, _, C, _, _, _, _],
      [_, _, _, D, _, _, _, _],
      [_, _, _, D, _, _, _, _],
      [_, _, _, C, _, _, _, _],
    ],
  };

  // Frame 4: Slightly narrower (3/4 turn other side)
  const frame4: PixelData = {
    width: 8,
    height: 8,
    pixels: [
      [_, _, _, C, C, _, _, _],
      [_, _, C, D, D, C, _, _],
      [_, C, D, L, L, D, C, _],
      [_, C, L, D, D, L, C, _],
      [_, C, L, D, D, L, C, _],
      [_, C, D, L, L, D, C, _],
      [_, _, C, D, D, C, _, _],
      [_, _, _, C, C, _, _, _],
    ],
  };

  return [frame1, frame2, frame3, frame4];
}

/**
 * Hourglass (8x10 pixels)
 */
export function getHourglassPixels(): PixelData {
  const W = '#c9a654'; // Wood/gold frame
  const S = '#f5e6c8'; // Sand
  const G = '#e0e0e0'; // Glass


  return {
    width: 8,
    height: 10,
    pixels: [
      [W, W, W, W, W, W, W, W],
      [_, W, G, G, G, G, W, _],
      [_, _, W, S, S, W, _, _],
      [_, _, _, S, S, _, _, _],
      [_, _, _, W, W, _, _, _],
      [_, _, _, W, W, _, _, _],
      [_, _, _, S, S, _, _, _],
      [_, _, W, S, S, W, _, _],
      [_, W, S, S, S, S, W, _],
      [W, W, W, W, W, W, W, W],
    ],
  };
}

/**
 * VIKINGS logo text (pixel art, 37x10 - perfectly centered)
 */
export function getLogoPixels(): PixelData {
  const L = '#ffe875'; // Bright gold highlight
  const C = '#c9a654'; // Pure gold
  const D = '#8a6518'; // Dark gold
  const R = '#b82a2a'; // Viking red underline
  const S = '#5c1414'; // Shadow red

  return {
    width: 37,
    height: 9,
    pixels: [
      // Row 0: Top highlights
      [L,_,_,_,L, _, L,L,L, _, L,_,_,_,L, _, L,L,L, _, L,_,_,_,L, _, _,L,L,L,_, _, _,L,L,L,L],
      // Row 1
      [C,_,_,_,C, _, _,C,_, _, C,_,_,L,_, _, _,C,_, _, C,L,_,_,C, _, C,_,_,_,_, _, C,_,_,_,_],
      // Row 2
      [C,_,_,_,C, _, _,C,_, _, C,C,L,_,_, _, _,C,_, _, C,_,L,_,C, _, C,_,L,L,L, _, _,L,L,L,_],
      // Row 3
      [_,C,_,C,_, _, _,C,_, _, C,_,C,_,_, _, _,C,_, _, C,_,_,L,C, _, C,_,_,_,C, _, _,_,_,_,C],
      // Row 4
      [_,C,_,C,_, _, _,C,_, _, C,_,_,C,_, _, _,C,_, _, C,_,_,_,C, _, C,_,_,_,C, _, C,_,_,_,C],
      // Row 5: Bottom bases & shadows
      [_,_,D,_,_, _, D,D,D, _, D,_,_,_,D, _, D,D,D, _, D,_,_,_,D, _, _,D,D,D,_, _, D,D,D,D,_],
      // Row 6: Spacing
      [_,_,_,_,_, _, _,_,_, _, _,_,_,_,_, _, _,_,_, _, _,_,_,_,_, _, _,_,_,_,_, _, _,_,_,_,_],
      // Row 7: Crimson banner underline
      [R,R,R,R,R, R, R,R,R, R, R,R,R,R,R, R, R,R,R, R, R,R,R,R,R, R, R,R,R,R,R, R, R,R,R,R,R],
      // Row 8: Crimson shadow
      [S,S,S,S,S, S, S,S,S, S, S,S,S,S,S, S, S,S,S, S, S,S,S,S,S, S, S,S,S,S,S, S, S,S,S,S,S],
    ],
  };
}

/**
 * Pixel-art fluffy cloud 1 (18x7)
 */
export function getCloud1Pixels(): PixelData {
  const H = '#ffffff'; // White highlight
  const M = '#c2d4ec'; // Soft sky blue-gray
  const S = '#819bb8'; // Bottom shadow

  return {
    width: 18,
    height: 7,
    pixels: [
      [_, _, _, _, _, H, H, H, H, _, _, _, _, _, _, _, _, _],
      [_, _, H, H, H, H, H, H, H, H, H, H, _, _, _, _, _, _],
      [_, H, H, H, M, M, M, M, H, H, H, H, H, H, _, _, _, _],
      [H, H, M, M, M, M, M, M, M, M, M, M, M, H, H, _, _, _],
      [H, M, M, M, M, M, M, M, M, M, M, M, M, M, M, H, _, _],
      [_, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, _],
      [_, _, _, S, S, S, S, S, S, S, S, S, S, _, _, _, _, _],
    ],
  };
}

/**
 * Pixel-art large cloud 2 (26x8)
 */
export function getCloud2Pixels(): PixelData {
  const H = '#ffffff';
  const M = '#c2d4ec';
  const S = '#819bb8';

  return {
    width: 26,
    height: 8,
    pixels: [
      [_, _, _, _, _, _, _, H, H, H, H, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
      [_, _, _, _, H, H, H, H, H, H, H, H, H, H, H, _, _, _, _, _, _, _, _, _, _, _],
      [_, _, H, H, H, H, H, H, M, M, M, M, M, H, H, H, H, H, H, _, _, _, _, _, _, _],
      [_, H, H, M, M, M, M, M, M, M, M, M, M, M, M, M, M, M, H, H, H, _, _, _, _, _],
      [H, H, M, M, M, M, M, M, M, M, M, M, M, M, M, M, M, M, M, M, H, H, _, _, _, _],
      [H, M, M, M, M, M, M, M, M, M, M, M, M, M, M, M, M, M, M, M, M, M, H, _, _, _],
      [_, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, _, _],
      [_, _, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, S, _, _, _, _],
    ],
  };
}

/**
 * Odin's raven / bird animation frames (3 frames, 8x5)
 */
export function getBirdFrames(): PixelData[] {
  const B = '#12131f'; // Raven black
  const W = '#2a2e47'; // Wing dark slate
  const Y = '#d99b26'; // Golden beak

  // Frame 1: Wings Up
  const frame1: PixelData = {
    width: 8,
    height: 5,
    pixels: [
      [_, W, _, _, _, _, W, _],
      [W, W, _, _, _, _, W, W],
      [_, W, B, B, B, B, W, _],
      [_, _, _, B, B, Y, _, _],
      [_, _, _, B, _, _, _, _],
    ],
  };

  // Frame 2: Wings Glide / Flat
  const frame2: PixelData = {
    width: 8,
    height: 5,
    pixels: [
      [_, _, _, _, _, _, _, _],
      [W, W, W, B, B, W, W, W],
      [_, _, B, B, B, B, Y, _],
      [_, _, _, B, B, _, _, _],
      [_, _, _, B, _, _, _, _],
    ],
  };

  // Frame 3: Wings Down
  const frame3: PixelData = {
    width: 8,
    height: 5,
    pixels: [
      [_, _, _, _, _, _, _, _],
      [_, _, B, B, B, B, Y, _],
      [W, W, B, B, B, B, W, W],
      [W, _, _, B, B, _, _, W],
      [_, _, _, B, _, _, _, _],
    ],
  };

  return [frame1, frame2, frame3];
}

/**
 * Battlefield background tile (16x16)
 */
export function getBattlefieldTile(): PixelData {
  const G1 = '#2d5a1e'; // Dark grass
  const G2 = '#3a7a28'; // Medium grass
  const G3 = '#4a9a35'; // Light grass
  const D = '#5a4a2e';  // Dirt


  return {
    width: 16,
    height: 16,
    pixels: [
      [G2,G2,G1,G2,G3,G2,G2,G1,G2,G2,G3,G2,G1,G2,G2,G2],
      [G2,G3,G2,G2,G2,G1,G2,G2,G3,G2,G2,G2,G2,G1,G2,G3],
      [G1,G2,G2,G3,G2,G2,G2,G2,G2,G1,G2,G3,G2,G2,G2,G2],
      [G2,G2,G2,G2,G1,G2,G3,G2,G2,G2,G2,G2,G1,G2,G3,G2],
      [G2,G3,G2,G2,G2,G2,G2,G1,G2,G2,G3,G2,G2,G2,G2,G1],
      [G2,G2,G1,G2,G2,G3,G2,G2,G2,G2,G2,G1,G2,G2,G2,G2],
      [G3,G2,G2,G2,G2,G2,G2,G2,G1,G2,G2,G2,G2,G3,G2,G2],
      [G2,G2,G2,G1,G2,G2,G2,G3,G2,G2,G2,G2,G2,G2,G1,G2],
      [G2,G1,G2,G2,G2,G2,D,D,D,D,G2,G1,G2,G2,G2,G2],
      [G2,G2,G2,G3,G2,D,D,D,D,D,D,G2,G2,G3,G2,G2],
      [G1,G2,G2,G2,G2,D,D,D,D,D,D,G2,G2,G2,G1,G2],
      [G2,G2,G3,G2,G2,G2,D,D,D,D,G2,G2,G3,G2,G2,G2],
      [G2,G2,G2,G2,G1,G2,G2,G2,G2,G2,G1,G2,G2,G2,G2,G3],
      [G3,G2,G2,G2,G2,G2,G3,G2,G2,G2,G2,G2,G2,G1,G2,G2],
      [G2,G1,G2,G3,G2,G2,G2,G1,G2,G3,G2,G2,G2,G2,G2,G2],
      [G2,G2,G2,G2,G2,G1,G2,G2,G2,G2,G1,G2,G3,G2,G2,G1],
    ],
  };
}

/**
 * Green paper icon (6x8)
 */
export function getGreenPaperPixels(): PixelData {
  const G = '#4caf50';
  const D = '#2e7d32';
  const L = '#81c784';


  return {
    width: 6,
    height: 8,
    pixels: [
      [_, G, G, G, G, _],
      [G, L, L, L, L, G],
      [G, L, D, D, L, G],
      [G, L, L, L, L, G],
      [G, L, D, D, L, G],
      [G, L, L, L, L, G],
      [G, L, L, L, L, G],
      [_, G, G, G, G, _],
    ],
  };
}

/**
 * Star paper icon (8x8)
 */
export function getStarPaperPixels(): PixelData {
  const Y = '#ffeb3b';
  const O = '#ff9800';
  const W = '#ffffff';


  return {
    width: 8,
    height: 8,
    pixels: [
      [_, _, _, Y, Y, _, _, _],
      [_, _, Y, O, O, Y, _, _],
      [Y, Y, O, W, W, O, Y, Y],
      [_, Y, O, W, W, O, Y, _],
      [_, _, Y, O, O, Y, _, _],
      [_, Y, _, Y, Y, _, Y, _],
      [Y, _, _, _, _, _, _, Y],
      [_, _, _, _, _, _, _, _],
    ],
  };
}

/**
 * Token icon (8x8)
 */
export function getTokenPixels(): PixelData {
  const B = '#42a5f5';
  const D = '#1565c0';
  const L = '#90caf9';


  return {
    width: 8,
    height: 8,
    pixels: [
      [_, _, B, B, B, B, _, _],
      [_, B, L, L, L, L, B, _],
      [B, L, D, L, L, D, L, B],
      [B, L, L, D, D, L, L, B],
      [B, L, L, D, D, L, L, B],
      [B, L, D, L, L, D, L, B],
      [_, B, L, L, L, L, B, _],
      [_, _, B, B, B, B, _, _],
    ],
  };
}

/**
 * Arrow sprite (10x3) — for first battle day effects.
 */
export function getArrowPixels(): PixelData {
  const W = '#8b7355'; // Wood shaft
  const T = '#a0a0a0'; // Steel tip
  const F = '#c9a654'; // Feather / fletching gold

  return {
    width: 10,
    height: 3,
    pixels: [
      [_, _, _, _, _, _, _, T, _, _],
      [F, F, W, W, W, W, W, T, T, T],
      [_, _, _, _, _, _, _, T, _, _],
    ],
  };
}

/**
 * Dragon animation frames (16x12, 3 frames) — for final battle day effects.
 */
export function getDragonFrames(): PixelData[] {
  const B = '#8b0000'; // Body dark red
  const R = '#cc2222'; // Body red
  const W = '#aa1111'; // Wing
  const E = '#ffe066'; // Eye
  const F = '#ff6600'; // Fire breath accent

  // Frame 1: Wings Up
  const frame1: PixelData = {
    width: 16,
    height: 12,
    pixels: [
      [_, _, _, W, _, _, _, _, _, _, _, _, W, _, _, _],
      [_, _, W, W, _, _, _, _, _, _, _, _, W, W, _, _],
      [_, W, W, _, _, _, _, _, _, _, _, _, _, W, W, _],
      [_, _, W, W, R, R, R, R, R, R, R, R, W, W, _, _],
      [_, _, _, R, R, R, R, R, R, R, R, R, R, _, _, _],
      [_, _, _, _, R, B, R, R, R, R, B, R, _, _, _, _],
      [_, _, _, _, _, R, R, R, R, R, R, _, _, _, _, _],
      [_, _, _, _, R, R, E, R, R, E, R, R, _, _, _, _],
      [_, _, _, _, _, R, R, R, R, R, R, F, _, _, _, _],
      [_, _, _, _, _, _, R, _, _, R, _, F, F, _, _, _],
      [_, _, _, _, _, _, R, _, _, R, _, _, _, _, _, _],
      [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
    ],
  };

  // Frame 2: Wings Glide (flat)
  const frame2: PixelData = {
    width: 16,
    height: 12,
    pixels: [
      [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
      [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
      [W, W, W, W, _, _, _, _, _, _, _, _, W, W, W, W],
      [_, _, _, W, R, R, R, R, R, R, R, R, W, _, _, _],
      [_, _, _, _, R, R, R, R, R, R, R, R, _, _, _, _],
      [_, _, _, _, R, B, R, R, R, R, B, R, _, _, _, _],
      [_, _, _, _, _, R, R, R, R, R, R, _, _, _, _, _],
      [_, _, _, _, R, R, E, R, R, E, R, R, _, _, _, _],
      [_, _, _, _, _, R, R, R, R, R, R, F, _, _, _, _],
      [_, _, _, _, _, _, R, _, _, R, _, F, F, _, _, _],
      [_, _, _, _, _, _, R, _, _, R, _, _, _, _, _, _],
      [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
    ],
  };

  // Frame 3: Wings Down
  const frame3: PixelData = {
    width: 16,
    height: 12,
    pixels: [
      [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
      [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
      [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
      [_, _, _, W, R, R, R, R, R, R, R, R, W, _, _, _],
      [_, _, W, W, R, R, R, R, R, R, R, R, W, W, _, _],
      [_, W, W, _, R, B, R, R, R, R, B, R, _, W, W, _],
      [_, _, W, _, _, R, R, R, R, R, R, _, _, W, _, _],
      [_, _, _, _, R, R, E, R, R, E, R, R, _, _, _, _],
      [_, _, _, _, _, R, R, R, R, R, R, F, _, _, _, _],
      [_, _, _, _, _, _, R, _, _, R, _, F, F, _, _, _],
      [_, _, _, _, _, _, R, _, _, R, _, _, _, _, _, _],
      [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
    ],
  };

  return [frame1, frame2, frame3];
}

/**
 * Standing dragon animation frames (56x48, 5 frames) — for idle battlefield / unauthorized screen.
 * Modelled directly from user's reference pixel art dragon:
 * - Front-facing seated dragon with 4 visible legs (two center front paws, two side haunches)
 * - Long neck, head with 3 crown horns (center + 2 side), cheek spikes, expressive eyes with glints
 * - Segmented cream belly plates down torso
 * - Large bat-like flapping wings
 * - Long tail curled to the side with spade tip
 * - Mouth roaring vibrant stream of fire
 */
export function getStandingDragonFrames(): PixelData[] {
  // Outline & deep shadows
  const O1 = '#76191d'; // Dark burgundy outline
  const K0 = '#2a080a'; // Deep mouth / pupil dark

  // Body warm orange & terracotta
  const B1 = '#fc8246'; // Bright coral / warm orange body
  const B2 = '#cf4129'; // Shaded terracotta body / wing membrane

  // Cream & peach belly plates & horns
  const C1 = '#fed8b0'; // Pale peach / cream belly & horns

  // Highlights & Teeth
  const W0 = '#ffffff'; // Specular eye glint & sharp fangs

  // Fiery breath
  const F1 = '#fff275'; // Bright yellow core flame
  const F2 = '#ff7b00'; // Vibrant dragonfire orange
  const F3 = '#e82415'; // Crimson flame licking tips

  const frame0: (string | null)[][] = [
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,_,_,_,_,_,_,_,_,O1,C1,O1,_,_,_,_,_,_,_,_,O1,O1,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,C1,C1,O1,O1,_,_,_,_,_,O1,C1,C1,C1,O1,_,_,_,_,_,O1,O1,C1,C1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,C1,C1,C1,C1,O1,_,_,_,_,O1,C1,C1,C1,O1,_,_,_,_,O1,C1,C1,C1,C1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,C1,C1,C1,C1,O1,O1,_,O1,O1,B1,C1,B1,O1,O1,_,O1,O1,C1,C1,C1,C1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,B1,C1,C1,C1,C1,O1,O1,B1,O1,B1,C1,B1,O1,B1,O1,O1,C1,C1,C1,C1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,B1,B1,C1,C1,O1,B1,B1,B1,B1,O1,B1,O1,B1,B1,B1,B1,O1,C1,C1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,B1,B1,O1,B1,B1,B1,B1,B1,B1,O1,B1,B1,B1,B1,B1,B1,O1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,O1,B1,B1,O1,B1,B1,B1,B1,B1,C1,C1,C1,B1,B1,B1,B1,B1,O1,B1,B1,O1,O1,O1,O1,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,C1,B1,O1,O1,O1,B1,B1,B1,B1,B1,C1,C1,C1,C1,C1,B1,B1,B1,B1,B1,O1,O1,O1,B1,C1,O1,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,C1,B1,O1,O1,B1,B1,O1,O1,B2,B1,C1,C1,C1,B1,B2,O1,O1,B1,B1,O1,O1,B1,C1,B1,O1,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,C1,B1,O1,B1,B1,O1,O1,O1,B1,B1,B1,B1,B1,O1,O1,O1,B1,B1,O1,B1,C1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,B1,O1,B1,B1,O1,C1,O1,B1,B1,B1,B1,B1,O1,C1,O1,B1,B1,O1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,B2,B1,B1,C1,O1,B1,B1,B1,B1,B1,O1,C1,B1,B1,B2,O1,O1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B2,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B2,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,B2,B1,B1,B1,B2,B2,B2,B1,B1,B1,B2,O1,O1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B2,B2,B1,B1,B1,B1,B2,O1,O1,O1,B2,B1,B1,B1,B1,B2,B2,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B2,C1,B1,B1,B1,B1,C1,C1,C1,C1,C1,B1,B1,B1,B1,C1,B2,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,_,_,_,O1,B1,B1,B1,B1,B1,B1,O1,C1,C1,C1,O1,B1,B1,B1,B1,B1,B1,O1,_,_,_,O1,O1,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,O1,_,_,_,O1,B1,B1,O1,O1,B1,B1,O1,B1,B1,B1,O1,B1,B1,O1,O1,B1,B1,O1,_,_,_,O1,O1,O1,O1,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,O1,O1,O1,_,_,O1,B2,B1,O1,B2,O1,B1,B1,B1,B1,B1,B1,B1,O1,B2,O1,B1,B2,O1,_,_,O1,O1,O1,O1,O1,O1,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,B2,O1,O1,O1,_,_,_,O1,B2,B2,B2,O1,B1,B1,B1,B1,B1,B1,B1,O1,B2,B2,B2,O1,_,_,_,O1,O1,O1,B2,O1,O1,O1,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,B2,B2,O1,B2,O1,O1,_,_,_,O1,B2,B2,B2,O1,B1,B1,B1,B1,B1,O1,B2,B2,B2,O1,_,_,_,O1,O1,B2,O1,B2,B2,O1,O1,O1,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,B2,B2,B2,O1,B2,B2,O1,O1,_,_,_,O1,O1,O1,B2,O1,B1,B1,B1,O1,B2,O1,O1,O1,_,_,_,O1,O1,B2,B2,O1,B2,B2,B2,O1,O1,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,B2,B2,B2,B2,O1,B2,B2,B2,O1,O1,O1,_,_,_,O1,B2,B2,O1,O1,O1,B2,B2,O1,_,_,_,O1,O1,O1,B2,B2,B2,O1,B2,B2,B2,B2,O1,O1,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,B2,B1,B2,B2,O1,B2,B2,B2,B2,B2,O1,O1,O1,O1,B2,B1,B2,B2,B2,B2,B2,B1,B2,O1,O1,O1,O1,B2,B2,B2,B2,B2,O1,B2,B2,B1,B2,O1,O1,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,O1,B2,B1,B1,B1,B2,O1,B2,B2,B1,B1,B1,B2,B2,O1,O1,B1,B1,B1,C1,C1,C1,B1,B1,B1,O1,O1,B2,B2,B1,B1,B1,B2,B2,O1,B2,B1,B1,B1,B2,O1,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,O1,O1,B1,B1,B1,B1,B1,O1,B2,B1,B1,B1,B1,B1,B1,O1,B2,B1,B1,B1,B1,B1,B1,B1,B1,B1,B2,O1,B1,B1,B1,B1,B1,B1,B2,O1,B1,B1,B1,B1,B1,O1,O1,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,O1,O1,B1,O1,O1,B1,B1,O1,B1,B1,B1,B1,B1,B1,O1,O1,B2,B1,B1,B1,C1,C1,C1,B1,B1,B1,B2,O1,O1,B1,B1,B1,B1,B1,B1,O1,B1,B1,O1,O1,B1,O1,O1,_,_],
    [_,_,O1,_,_,_,_,_,_,_,_,O1,O1,O1,_,_,O1,B1,O1,B1,B1,O1,O1,O1,O1,O1,O1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,O1,O1,O1,O1,O1,O1,B1,B1,O1,B1,O1,_,_,O1,O1,O1,_,_],
    [_,_,O1,O1,_,_,_,_,_,_,_,O1,O1,_,_,_,_,O1,O1,B1,O1,_,_,_,_,O1,B2,B1,B1,B1,C1,C1,C1,C1,C1,B1,B1,B1,B2,O1,_,_,_,_,O1,B1,O1,O1,_,_,_,_,O1,O1,_,_],
    [_,_,O1,C1,O1,O1,_,_,_,_,_,O1,_,_,_,_,_,_,O1,O1,_,_,_,_,O1,O1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,O1,O1,_,_,_,_,O1,O1,_,_,_,_,_,_,O1,_,_],
    [_,_,O1,B1,C1,C1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,B1,B1,B1,B1,B1,C1,C1,C1,B1,B1,B1,B1,B1,O1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,O1,B1,O1,B1,C1,O1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,O1,B1,B1,B1,B1,B1,C1,C1,C1,B2,B1,B1,B1,B1,O1,B1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,O1,B1,O1,O1,B1,C1,C1,O1,O1,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,O1,B2,B1,B1,B1,B1,B1,B1,B1,B2,B2,B1,B1,B2,O1,B1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,O1,B1,O1,B1,O1,B1,C1,C1,C1,O1,_,_,_,_,_,_,_,_,_,_,O1,B1,B1,B1,O1,B1,B1,B1,B1,O1,C1,O1,B2,B2,B1,B1,O1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,O1,B1,O1,B1,O1,O1,O1,O1,_,_,_,_,_,_,_,_,_,_,O1,B1,B1,B1,B1,O1,B1,B1,B1,B1,O1,C1,O1,B2,B1,B1,B1,O1,B1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,O1,C1,B1,O1,B2,B2,B1,B1,O1,O1,_,_,_,_,_,_,_,_,O1,B1,B1,B1,B1,O1,B2,B1,B1,B1,O1,C1,O1,B2,B1,B1,B2,O1,B1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,O1,C1,C1,B1,O1,B2,B2,B1,B1,B1,O1,O1,O1,_,_,_,_,O1,B1,B1,B1,B1,B1,B2,O1,B1,B1,B1,B2,O1,B2,B2,B1,B1,O1,B2,B1,B1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,O1,C1,C1,O1,O1,B2,B2,B2,B1,B1,B1,B1,O1,O1,O1,O1,O1,B1,B1,B1,B1,B1,B2,O1,B1,B1,B1,B1,O1,B2,B2,B1,B1,O1,B2,B1,B1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,O1,C1,O1,_,_,O1,O1,B2,B2,B2,B1,B1,B1,B1,B1,B2,O1,B1,B1,B1,B1,B2,B2,O1,B2,B1,B1,B1,O1,B2,B1,B1,B1,O1,B2,B2,B1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,O1,_,_,_,_,_,O1,O1,B2,B2,B2,B2,B2,B2,B2,O1,B1,B1,B2,B2,B2,B2,O1,B2,B1,B1,B1,O1,B2,B1,B1,B2,O1,B2,B2,B2,B2,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,B2,B2,B2,O1,O1,O1,O1,O1,O1,B2,B2,O1,O1,B2,B1,B1,O1,B2,B1,B2,O1,O1,B2,B2,O1,O1,O1,O1,O1,O1,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,B1,B1,B1,B1,B1,B1,O1,B2,O1,B1,B1,B1,B1,B1,O1,B1,B1,B1,O1,B2,O1,B1,B1,B1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,O1,B1,O1,B1,B1,B1,O1,O1,B1,O1,B1,O1,B1,O1,B1,O1,B1,O1,O1,B1,B1,B1,O1,B1,O1,B1,O1,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
  ];

  const frame1: (string | null)[][] = [
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,_,_,_,_,_,_,_,_,O1,C1,O1,_,_,_,_,_,_,_,_,O1,O1,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,C1,C1,O1,O1,_,_,_,_,_,O1,C1,C1,C1,O1,_,_,_,_,_,O1,O1,C1,C1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,C1,C1,C1,C1,O1,_,_,_,_,O1,C1,C1,C1,O1,_,_,_,_,O1,C1,C1,C1,C1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,C1,C1,C1,C1,O1,O1,_,O1,O1,B1,C1,B1,O1,O1,_,O1,O1,C1,C1,C1,C1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,B1,C1,C1,C1,C1,O1,O1,B1,O1,B1,C1,B1,O1,B1,O1,O1,C1,C1,C1,C1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,B1,B1,C1,C1,O1,B1,B1,B1,B1,O1,B1,O1,B1,B1,B1,B1,O1,C1,C1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,B1,B1,O1,B1,B1,B1,B1,B1,B1,O1,B1,B1,B1,B1,B1,B1,O1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,O1,B1,B1,O1,B1,B1,B1,B1,B1,C1,C1,C1,B1,B1,B1,B1,B1,O1,B1,B1,O1,O1,O1,O1,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,C1,B1,O1,O1,O1,B1,B1,B1,B1,B1,C1,C1,C1,C1,C1,B1,B1,B1,B1,B1,O1,O1,O1,B1,C1,O1,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,C1,B1,O1,O1,B1,B1,O1,O1,B2,B1,C1,C1,C1,B1,B2,O1,O1,B1,B1,O1,O1,B1,C1,B1,O1,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,C1,B1,O1,B1,B1,O1,O1,O1,B1,B1,B1,B1,B1,O1,O1,O1,B1,B1,O1,B1,C1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,_,_,O1,B1,B1,O1,B1,B1,O1,C1,O1,B1,B1,B1,B1,B1,O1,C1,O1,B1,B1,O1,B1,B1,O1,_,_,O1,O1,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,O1,_,_,_,O1,O1,O1,B2,B1,B1,C1,O1,B1,B1,B1,B1,B1,O1,C1,B1,B1,B2,O1,O1,O1,_,_,_,O1,O1,O1,O1,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,O1,O1,O1,_,_,_,_,O1,B2,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B2,O1,_,_,_,_,O1,O1,O1,O1,O1,O1,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,B2,O1,O1,O1,_,_,_,_,O1,O1,O1,B2,B1,B1,B1,B2,B2,B2,B1,B1,B1,B2,O1,O1,O1,_,_,_,_,O1,O1,O1,B2,O1,O1,O1,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,B2,B2,O1,B2,O1,O1,_,_,O1,B2,B2,B1,B1,B1,B1,B2,O1,O1,O1,B2,B1,B1,B1,B1,B2,B2,O1,_,_,O1,O1,B2,O1,B2,B2,O1,O1,O1,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,B2,B2,B2,O1,B2,B2,O1,O1,_,_,_,C1,B1,B1,B1,B1,C1,C1,C1,C1,C1,B1,B1,B1,_,_,_,_,_,O1,O1,B2,B2,O1,B2,B2,B2,O1,O1,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,O1,O1,B2,B2,B2,B2,O1,B2,B2,B2,O1,O1,O1,_,B1,B1,B1,B1,B1,O1,C1,O1,K0,F1,K0,O1,B1,_,_,_,O1,O1,O1,B2,B2,B2,O1,B2,B2,B2,B2,O1,O1,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,O1,O1,B2,B1,B2,B2,O1,B2,B2,B2,B2,B2,O1,O1,O1,O1,O1,B1,B1,O1,B1,B1,O1,F2,O1,B1,O1,O1,O1,O1,O1,B2,B2,B2,B2,B2,O1,B2,B2,B1,B2,O1,O1,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,O1,B2,B1,B1,B1,B2,O1,B2,B2,B1,B1,B1,B2,B2,O1,O1,B2,O1,B1,B1,B1,B1,B1,B1,B1,O1,B2,O1,O1,B2,B2,B1,B1,B1,B2,B2,O1,B2,B1,B1,B1,B2,O1,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,O1,O1,B1,B1,B1,B1,B1,O1,B2,B1,B1,B1,B1,B1,B1,O1,B2,O1,B1,B1,B1,B1,B1,B1,B1,O1,B2,O1,B1,B1,B1,B1,B1,B1,B2,O1,B1,B1,B1,B1,B1,O1,O1,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,O1,O1,B1,O1,O1,B1,B1,O1,B1,B1,B1,B1,B1,B1,O1,O1,B2,B2,O1,B1,B1,B1,B1,B1,O1,B2,B2,O1,O1,B1,B1,B1,B1,B1,B1,O1,B1,B1,O1,O1,B1,O1,O1,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,_,_,O1,B1,O1,B1,B1,O1,O1,O1,O1,O1,O1,O1,O1,B2,O1,B1,B1,B1,O1,B2,O1,O1,O1,O1,O1,O1,O1,O1,B1,B1,O1,B1,O1,_,_,O1,O1,O1,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,O1,O1,_,_,_,_,O1,O1,B1,O1,_,_,_,_,O1,B2,_,O1,B2,B2,O1,O1,O1,B2,B2,O1,_,B2,O1,_,_,_,_,O1,B1,O1,O1,_,_,_,_,O1,O1,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,O1,_,_,_,_,_,_,O1,O1,_,_,_,_,O1,O1,O1,O1,B2,B1,B2,B2,B2,B2,B2,B1,B2,O1,B2,O1,O1,_,_,_,_,O1,O1,_,_,_,_,_,_,O1,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,B1,B1,B1,C1,C1,C1,B1,B1,B1,O1,B2,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,B2,B1,B1,B1,B1,B1,B1,B1,B1,B1,B2,B2,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,B2,B1,B1,B1,C1,C1,C1,B1,B1,B1,B2,B2,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,O1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B2,B1,B1,B1,C1,C1,C1,C1,C1,B1,B1,B1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,O1,C1,O1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,O1,B1,C1,C1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,B1,B1,B1,B1,B1,C1,C1,C1,B1,B1,B1,B1,B1,O1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,O1,B1,O1,B1,C1,O1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,O1,B1,B1,B1,B1,B1,C1,C1,C1,B2,B1,B1,B1,B1,O1,B1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,O1,B1,O1,O1,B1,C1,C1,O1,O1,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,O1,B2,B1,B1,B1,B1,B1,B1,B1,B2,B2,B1,B1,B2,O1,B1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,O1,B1,O1,B1,O1,B1,C1,C1,C1,O1,_,_,_,_,_,_,_,_,_,_,O1,B1,B1,B1,O1,B1,B1,B1,B1,O1,C1,O1,B2,B2,B1,B1,O1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,O1,B1,O1,B1,O1,O1,O1,O1,_,_,_,_,_,_,_,_,_,_,O1,B1,B1,B1,B1,O1,B1,B1,B1,B1,O1,C1,O1,B2,B1,B1,B1,O1,B1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,O1,C1,B1,O1,B2,B2,B1,B1,O1,O1,_,_,_,_,_,_,_,_,O1,B1,B1,B1,B1,O1,B2,B1,B1,B1,O1,C1,O1,B2,B1,B1,B2,O1,B1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,O1,C1,C1,B1,O1,B2,B2,B1,B1,B1,O1,O1,O1,_,_,_,_,O1,B1,B1,B1,B1,B1,B2,O1,B1,B1,B1,B2,O1,B2,B2,B1,B1,O1,B2,B1,B1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,O1,C1,C1,O1,O1,B2,B2,B2,B1,B1,B1,B1,O1,O1,O1,O1,O1,B1,B1,B1,B1,B1,B2,O1,B1,B1,B1,B1,O1,B2,B2,B1,B1,O1,B2,B1,B1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,O1,C1,O1,_,_,O1,O1,B2,B2,B2,B1,B1,B1,B1,B1,B2,O1,B1,B1,B1,B1,B2,B2,O1,B2,B1,B1,B1,O1,B2,B1,B1,B1,O1,B2,B2,B1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,O1,_,_,_,_,_,O1,O1,B2,B2,B2,B2,B2,B2,B2,O1,B1,B1,B2,B2,B2,B2,O1,B2,B1,B1,B1,O1,B2,B1,B1,B2,O1,B2,B2,B2,B2,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,B2,B2,B2,O1,O1,O1,O1,O1,O1,B2,B2,O1,O1,B2,B1,B1,O1,B2,B1,B2,O1,O1,B2,B2,O1,O1,O1,O1,O1,O1,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,B1,B1,B1,B1,B1,B1,O1,B2,O1,B1,B1,B1,B1,B1,O1,B1,B1,B1,O1,B2,O1,B1,B1,B1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,O1,B1,O1,B1,B1,B1,O1,O1,B1,O1,B1,O1,B1,O1,B1,O1,B1,O1,O1,B1,B1,B1,O1,B1,O1,B1,O1,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
  ];

  const frame2: (string | null)[][] = [
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,_,_,_,_,_,_,_,_,O1,C1,O1,_,_,_,_,_,_,_,_,O1,O1,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,C1,C1,O1,O1,_,_,_,_,_,O1,C1,C1,C1,O1,_,_,_,_,_,O1,O1,C1,C1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,C1,C1,C1,C1,O1,_,_,_,_,O1,C1,C1,C1,O1,_,_,_,_,O1,C1,C1,C1,C1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,C1,C1,C1,C1,O1,O1,_,O1,O1,B1,C1,B1,O1,O1,_,O1,O1,C1,C1,C1,C1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,B1,C1,C1,C1,C1,O1,O1,B1,O1,B1,C1,B1,O1,B1,O1,O1,C1,C1,C1,C1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,B1,B1,C1,C1,O1,B1,B1,B1,B1,O1,B1,O1,B1,B1,B1,B1,O1,C1,C1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,B1,B1,O1,B1,B1,B1,B1,B1,B1,O1,B1,B1,B1,B1,B1,B1,O1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,O1,B1,B1,O1,B1,B1,B1,B1,B1,C1,C1,C1,B1,B1,B1,B1,B1,O1,B1,B1,O1,O1,O1,O1,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,C1,B1,O1,O1,O1,B1,B1,B1,B1,B1,C1,C1,C1,C1,C1,B1,B1,B1,B1,B1,O1,O1,O1,B1,C1,O1,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,C1,B1,O1,O1,B1,B1,O1,O1,B2,B1,C1,C1,C1,B1,B2,O1,O1,B1,B1,O1,O1,B1,C1,B1,O1,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,C1,B1,O1,B1,B1,O1,O1,O1,B1,B1,B1,B1,B1,O1,O1,O1,B1,B1,O1,B1,C1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,B1,O1,B1,B1,O1,C1,O1,B1,B1,B1,B1,B1,O1,C1,O1,B1,B1,O1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,B2,B1,B1,C1,O1,B1,B1,B1,B1,B1,O1,C1,B1,B1,B2,O1,O1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B2,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B2,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,B2,B1,B1,B1,B2,B2,B2,B1,B1,B1,B2,O1,O1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B2,B2,B1,B1,B1,B1,B2,O1,O1,O1,B2,B1,B1,B1,B1,B2,B2,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,C1,B1,B1,B1,B1,C1,C1,C1,C1,C1,B1,B1,B1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,B1,B1,B1,B1,B1,O1,O1,W0,K0,F1,K0,W0,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B2,B1,O1,O1,B1,B1,O1,O1,F3,F1,W0,F1,F3,O1,_,_,B2,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,_,_,_,_,O1,B2,B1,O1,B2,O1,B1,O1,F3,F3,F2,F2,F1,F1,F2,F2,F3,F3,O1,_,_,_,_,O1,O1,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,O1,_,_,_,_,_,O1,O1,F1,O1,O1,O1,F3,F2,F2,F2,F2,F1,F1,F2,F2,F2,F2,F3,O1,_,O1,F1,O1,O1,O1,O1,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,O1,O1,O1,_,_,_,_,O1,O1,B2,B2,O1,F3,F3,F2,F2,F1,F1,F1,F1,F1,F1,F2,F2,F3,F3,O1,_,O1,O1,O1,O1,O1,O1,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,B2,O1,O1,O1,_,_,_,O1,F2,O1,O1,O1,F3,F2,F2,F1,F1,F1,F1,W0,W0,F1,F1,F1,F1,F2,F2,F3,O1,O1,O1,F2,O1,O1,O1,O1,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,B2,B2,O1,B2,O1,O1,_,_,_,_,O1,O1,F3,F2,F2,F1,F1,F1,F1,W0,W0,W0,W0,F1,F1,F1,F1,F2,F2,F3,O1,B2,O1,B2,B2,O1,O1,O1,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,B2,B2,B2,O1,B2,B2,O1,O1,O1,F3,O1,O1,O1,F3,F3,F2,F2,F1,F1,W0,W0,W0,W0,W0,W0,F1,F1,F2,F2,F3,F3,O1,B2,O1,F3,O1,B2,O1,O1,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,O1,O1,B2,B2,B2,B2,O1,B2,B2,B2,O1,O1,O1,_,O1,O1,O1,F3,F2,F2,F1,F1,F1,W0,W0,W0,W0,F1,F1,F1,F2,F2,F3,O1,B2,B2,O1,B2,B2,B2,B2,O1,O1,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,O1,O1,B2,B1,B2,B2,O1,B2,B2,B2,B2,B2,O1,O1,O1,O1,O1,O1,F3,F2,F2,F1,F1,F1,F1,F1,F1,F1,F1,F2,F2,F3,O1,O1,B2,B2,O1,B2,B2,B1,B2,O1,O1,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,O1,B2,B1,B1,B1,B2,O1,B2,B2,B1,B1,B1,B2,B2,O1,O1,O1,F3,F3,F2,F2,F2,F2,F2,F2,F2,F2,F2,F2,F2,F2,F3,F3,O1,B2,B2,O1,B2,B1,B1,B1,B2,O1,_,_],
    [_,_,O1,_,_,_,_,_,_,_,_,O1,O1,B1,B1,B1,B1,B1,O1,B2,B1,B1,B1,B1,B1,B1,O1,B1,O1,F3,F2,F2,F2,F2,F2,B1,B1,F2,F2,F2,F2,F2,F2,F3,O1,B2,O1,B1,B1,B1,B1,B1,O1,O1,_,_],
    [_,_,O1,O1,_,_,_,_,_,_,_,O1,O1,B1,O1,O1,B1,B1,O1,B1,B1,B1,B1,B1,B1,O1,O1,B1,O1,F3,F3,F2,F2,F3,F3,B1,B1,F3,F3,F2,F2,F3,F3,O1,B1,B1,O1,B1,B1,O1,O1,B1,O1,O1,_,_],
    [_,_,O1,C1,O1,O1,_,_,_,_,_,O1,O1,O1,_,_,O1,B1,O1,B1,B1,O1,O1,O1,O1,O1,O1,B1,B1,O1,F3,F2,F3,O1,B1,B1,B1,B1,O1,F3,F2,F3,O1,O1,B1,B1,O1,B1,O1,_,_,O1,O1,O1,_,_],
    [_,_,O1,B1,C1,C1,O1,_,_,_,_,O1,O1,_,_,_,_,O1,O1,B1,O1,_,_,_,O1,O1,B1,B1,B1,B1,O1,F3,O1,C1,B1,B1,B1,B1,B1,O1,F3,O1,_,_,O1,B1,O1,O1,_,_,_,_,O1,O1,_,_],
    [_,_,O1,B1,O1,B1,C1,O1,O1,_,_,O1,_,_,_,_,_,_,O1,O1,_,_,_,O1,O1,O1,B1,O1,F2,O1,B1,C1,C1,C1,B2,B1,B1,B1,B1,O1,O1,O1,_,_,_,O1,O1,_,_,_,_,_,_,O1,_,_],
    [_,_,O1,B1,O1,O1,B1,C1,C1,O1,O1,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,O1,B2,B1,B1,B1,B1,B1,B1,B1,B2,B2,B1,B1,B2,O1,O1,F1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,O1,B1,O1,B1,O1,B1,C1,C1,C1,O1,_,_,_,_,_,_,_,_,_,_,O1,B1,B1,B1,O1,B1,B1,B1,B1,O1,C1,O1,B2,B2,B1,B1,O1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,O1,B1,O1,B1,O1,O1,O1,O1,_,_,_,_,_,_,_,_,_,_,O1,B1,B1,B1,B1,O1,B1,B1,B1,B1,O1,C1,O1,B2,B1,B1,B1,O1,B1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,O1,C1,B1,O1,B2,B2,B1,B1,O1,O1,_,_,_,_,_,_,_,_,O1,B1,B1,B1,B1,O1,B2,B1,B1,B1,O1,C1,O1,B2,B1,B1,B2,O1,B1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,O1,C1,C1,B1,O1,B2,B2,B1,B1,B1,O1,O1,O1,_,_,_,_,O1,B1,B1,B1,B1,B1,B2,O1,B1,B1,B1,B2,O1,B2,B2,B1,B1,O1,B2,B1,B1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,O1,C1,C1,O1,O1,B2,B2,B2,B1,B1,B1,B1,O1,O1,O1,O1,O1,B1,B1,B1,B1,B1,B2,O1,B1,B1,B1,B1,O1,B2,B2,B1,B1,O1,B2,B1,B1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,O1,C1,O1,_,_,O1,O1,B2,B2,B2,B1,B1,B1,B1,B1,B2,O1,B1,B1,B1,B1,B2,B2,O1,B2,B1,B1,B1,O1,B2,B1,B1,B1,O1,B2,B2,B1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,O1,_,_,_,_,_,O1,O1,B2,B2,B2,B2,B2,B2,B2,O1,B1,B1,B2,B2,B2,B2,O1,B2,B1,B1,B1,O1,B2,B1,B1,B2,O1,B2,B2,B2,B2,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,B2,B2,B2,O1,O1,O1,O1,O1,O1,B2,B2,O1,O1,B2,B1,B1,O1,B2,B1,B2,O1,O1,B2,B2,O1,O1,O1,O1,O1,O1,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,B1,B1,B1,B1,B1,B1,O1,B2,O1,B1,B1,B1,B1,B1,O1,B1,B1,B1,O1,B2,O1,B1,B1,B1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,O1,B1,O1,B1,B1,B1,O1,O1,B1,O1,B1,O1,B1,O1,B1,O1,B1,O1,O1,B1,B1,B1,O1,B1,O1,B1,O1,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
  ];

  const frame3: (string | null)[][] = [
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,_,_,_,_,_,_,_,_,O1,C1,O1,_,_,_,_,_,_,_,_,O1,O1,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,C1,C1,O1,O1,_,_,_,_,_,O1,C1,C1,C1,O1,_,_,_,_,_,O1,O1,C1,C1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,C1,C1,C1,C1,O1,_,_,_,_,O1,C1,C1,C1,O1,_,_,_,_,O1,C1,C1,C1,C1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,C1,C1,C1,C1,O1,O1,_,O1,O1,B1,C1,B1,O1,O1,_,O1,O1,C1,C1,C1,C1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,B1,C1,C1,C1,C1,O1,O1,B1,O1,B1,C1,B1,O1,B1,O1,O1,C1,C1,C1,C1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,B1,B1,C1,C1,O1,B1,B1,B1,B1,O1,B1,O1,B1,B1,B1,B1,O1,C1,C1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,B1,B1,O1,B1,B1,B1,B1,B1,B1,O1,B1,B1,B1,B1,B1,B1,O1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,O1,B1,B1,O1,B1,B1,B1,B1,B1,C1,C1,C1,B1,B1,B1,B1,B1,O1,B1,B1,O1,O1,O1,O1,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,C1,B1,O1,O1,O1,B1,B1,B1,B1,B1,C1,C1,C1,C1,C1,B1,B1,B1,B1,B1,O1,O1,O1,B1,C1,O1,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,C1,B1,O1,O1,B1,B1,O1,O1,B2,B1,C1,C1,C1,B1,B2,O1,O1,B1,B1,O1,O1,B1,C1,B1,O1,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,C1,B1,O1,B1,B1,O1,O1,O1,B1,B1,B1,B1,B1,O1,O1,O1,B1,B1,O1,B1,C1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,B1,O1,B1,B1,O1,C1,O1,B1,B1,B1,B1,B1,O1,C1,O1,B1,B1,O1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,B2,B1,B1,C1,O1,B1,B1,B1,B1,B1,O1,C1,B1,B1,B2,O1,O1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B2,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B2,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,B2,B1,B1,B1,B2,B2,B2,B1,B1,B1,B2,O1,O1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B2,B2,B1,B1,B1,B1,B2,O1,O1,O1,B2,B1,B1,B1,B1,B2,B2,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B2,C1,B1,B1,B1,B1,C1,C1,C1,C1,C1,B1,B1,B1,B1,C1,B2,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,_,_,_,O1,B1,B1,B1,B1,B1,B1,O1,C1,C1,O1,F1,O1,B1,B1,B1,B1,B1,O1,_,_,_,O1,O1,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,O1,_,_,_,O1,B1,B1,O1,O1,B1,B1,O1,B1,B1,O1,F2,O1,B1,O1,O1,B1,B1,O1,_,_,_,O1,O1,O1,O1,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,O1,O1,O1,_,_,O1,B2,B1,O1,O1,F1,O1,B1,B1,B1,B1,B1,B1,O1,B2,O1,B1,B2,O1,_,_,O1,O1,O1,O1,O1,O1,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,B2,O1,O1,O1,_,_,_,O1,B2,B2,B2,O1,B1,B1,B1,B1,B1,B1,B1,O1,B2,B2,B2,O1,F2,O1,_,O1,O1,O1,B2,O1,O1,O1,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,B2,B2,O1,B2,O1,O1,_,_,_,O1,B2,B2,B2,O1,F3,F3,F2,F2,F3,F3,O1,B2,B2,O1,_,_,_,O1,O1,B2,O1,B2,B2,O1,O1,O1,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,B2,B2,B2,O1,B2,B2,O1,O1,_,_,_,O1,O1,O1,F3,F2,F2,F1,F1,F2,F2,F3,O1,O1,_,_,_,O1,O1,B2,B2,O1,B2,B2,B2,O1,O1,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,B2,B2,B2,B2,O1,B2,B2,B2,O1,O1,O1,_,_,O1,F3,F2,F2,F1,F1,F1,F1,F2,F2,F3,O1,_,O1,O1,O1,B2,B2,B2,O1,B2,B2,B2,B2,O1,O1,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,B2,B1,B2,B2,O1,B2,B2,B2,B2,B2,O1,O1,O1,O1,F3,F2,F2,F1,F1,F1,F1,F2,F2,F3,O1,O1,O1,B2,B2,B2,B2,B2,O1,B2,B2,B1,B2,O1,O1,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,O1,B2,B1,B1,B1,B2,O1,B2,B2,B1,B1,B1,B2,B2,O1,O1,O1,F3,F2,F2,F1,F1,F2,F2,F3,O1,O1,B2,B2,B1,B1,B1,B2,B2,O1,B2,B1,B1,B1,B2,O1,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,O1,O1,B1,B1,B1,B1,B1,O1,B2,B1,B1,B1,B1,B1,B1,O1,B2,B1,O1,F3,F3,F2,F2,F3,F3,O1,B2,O1,B1,B1,B1,B1,B1,B1,B2,O1,B1,B1,B1,B1,B1,O1,O1,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,O1,O1,B1,O1,O1,B1,B1,O1,B1,B1,B1,B1,B1,B1,O1,O1,B2,B1,B1,O1,F3,F2,F3,O1,B1,B1,B2,O1,O1,B1,B1,B1,B1,B1,B1,O1,B1,B1,O1,O1,B1,O1,O1,_,_],
    [_,_,O1,_,_,_,_,_,_,_,_,O1,O1,O1,_,_,O1,B1,O1,B1,B1,O1,O1,O1,O1,O1,O1,O1,F2,O1,B1,B1,B1,B1,B1,B1,B1,B1,O1,O1,O1,O1,O1,O1,B1,B1,O1,B1,O1,_,_,O1,O1,O1,_,_],
    [_,_,O1,O1,_,_,_,_,_,_,_,O1,O1,_,_,_,_,O1,O1,B1,O1,_,_,_,_,O1,B2,B1,B1,B1,C1,C1,C1,C1,C1,B1,B1,B1,O1,F1,O1,_,_,_,O1,B1,O1,O1,_,_,_,_,O1,O1,_,_],
    [_,_,O1,C1,O1,O1,_,_,_,_,_,O1,_,_,_,_,_,_,O1,O1,_,_,_,_,O1,O1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,B1,O1,O1,_,_,_,_,O1,O1,_,_,_,_,_,_,O1,_,_],
    [_,_,O1,B1,C1,C1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,B1,B1,B1,B1,B1,C1,C1,O1,F2,O1,B1,B1,B1,O1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,O1,B1,O1,B1,C1,O1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,O1,B1,B1,B1,B1,B1,C1,C1,C1,B2,B1,B1,B1,B1,O1,B1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,O1,B1,O1,O1,B1,C1,C1,O1,O1,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,O1,B2,B1,B1,B1,B1,B1,B1,B1,B2,B2,B1,B1,B2,O1,B1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,O1,B1,O1,B1,O1,B1,C1,C1,C1,O1,_,_,_,_,_,_,_,_,_,_,O1,B1,B1,B1,O1,B1,B1,B1,B1,O1,C1,O1,B2,B2,B1,B1,O1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,O1,B1,O1,B1,O1,O1,O1,O1,_,_,_,_,_,_,_,_,_,_,O1,B1,B1,B1,B1,O1,B1,B1,B1,B1,O1,C1,O1,B2,B1,B1,B1,O1,B1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,O1,C1,B1,O1,B2,B2,B1,B1,O1,O1,_,_,_,_,_,_,_,_,O1,B1,B1,B1,B1,O1,B2,B1,B1,B1,O1,C1,O1,B2,B1,B1,B2,O1,B1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,O1,C1,C1,B1,O1,B2,B2,B1,B1,B1,O1,O1,O1,_,_,_,_,O1,B1,B1,B1,B1,B1,B2,O1,B1,B1,B1,B2,O1,B2,B2,B1,B1,O1,B2,B1,B1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,O1,C1,C1,O1,O1,B2,B2,B2,B1,B1,B1,B1,O1,O1,O1,O1,O1,B1,B1,B1,B1,B1,B2,O1,B1,B1,B1,B1,O1,B2,B2,B1,B1,O1,B2,B1,B1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,O1,C1,O1,_,_,O1,O1,B2,B2,B2,B1,B1,B1,B1,B1,B2,O1,B1,B1,B1,B1,B2,B2,O1,B2,B1,B1,B1,O1,B2,B1,B1,B1,O1,B2,B2,B1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,O1,_,_,_,_,_,O1,O1,B2,B2,B2,B2,B2,B2,B2,O1,B1,B1,B2,B2,B2,B2,O1,B2,B1,B1,B1,O1,B2,B1,B1,B2,O1,B2,B2,B2,B2,B1,B1,O1,_,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,B2,B2,B2,O1,O1,O1,O1,O1,O1,B2,B2,O1,O1,B2,B1,B1,O1,B2,B1,B2,O1,O1,B2,B2,O1,O1,O1,O1,O1,O1,_,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,B1,B1,B1,B1,B1,B1,O1,B2,O1,B1,B1,B1,B1,B1,O1,B1,B1,B1,O1,B2,O1,B1,B1,B1,B1,B1,B1,O1,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,B1,O1,B1,O1,B1,B1,B1,O1,O1,B1,O1,B1,O1,B1,O1,B1,O1,B1,O1,O1,B1,B1,B1,O1,B1,O1,B1,O1,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,O1,_,_,_,_,_,_,_,_,_],
    [_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_,_],
  ];

  return [
    { width: 56, height: 48, pixels: frame0 },
    { width: 56, height: 48, pixels: frame0 }, // pause on rest
    { width: 56, height: 48, pixels: frame1 }, // wings up & spark
    { width: 56, height: 48, pixels: frame2 }, // wings down & roaring fire
    { width: 56, height: 48, pixels: frame3 }, // wings mid & flame billow
  ];
}


/**
 * Fire particle (4x4) — small rising flame for final battle.
 */
export function getFireParticlePixels(): PixelData {
  const Y = '#ffe066'; // Yellow hot core
  const O = '#ff9900'; // Orange flame
  const R = '#cc3300'; // Red outer

  return {
    width: 4,
    height: 4,
    pixels: [
      [_, Y, _, _],
      [O, Y, O, _],
      [R, O, O, R],
      [_, R, R, _],
    ],
  };
}

/**
 * Lightning bolt (3x12) — for first battle day effects.
 */
export function getLightningPixels(): PixelData {
  const L = '#ffffff'; // Bright white
  const B = '#88bbff'; // Blue glow

  return {
    width: 3,
    height: 12,
    pixels: [
      [_, L, _],
      [_, L, _],
      [B, L, _],
      [_, L, _],
      [_, L, B],
      [_, L, _],
      [B, L, _],
      [_, L, _],
      [_, L, _],
      [B, L, B],
      [_, L, _],
      [_, B, _],
    ],
  };
}
